import { ArrayHelpers, FastField, FieldArray, FieldProps, FormikProps, useFormikContext } from 'formik';
import { DeclineIcon, PhoneIcon } from '@synergy/theme';
import { FormattedMessage, useIntl } from 'react-intl';
import { Fragment } from 'react';
import { Grid, IconButton } from '@mui/material';
import { PhoneType } from '@synergy/models';
import { PoolButton, PoolSelect, PoolTextField, PoolTypography, SelectOption } from '@synergy/components';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { PhoneFormValues } from '../../models/phone-form-values';
import { SubtitleWithIcon } from '../subtitle-with-icon';

type PhoneArrayHelpers = ArrayHelpers & {
    form: FormikProps<OverviewPhoneFormValues>;
    name: string;
};

interface OverviewPhoneFormValues {
    mainPhone: string;
    phones: PhoneFormValues[];
}

// The fixed entry's type, standing where the others show a select. The 17/24
// padding it carried was measured against a 38-pixel field; beside a 29-pixel
// one it sat low and 24 pixels off to the right.
const useStyles = makeStyles(() => ({
    static: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 29,
        fontSize: '12.5px',
    },
}));

const phoneTypeOptions: SelectOption[] = [
    {
        value: PhoneType.Work,
        displayValue: <FormattedMessage id="app.select.option.work" />,
    },
    {
        value: PhoneType.Home,
        displayValue: <FormattedMessage id="app.select.option.private" />,
    },
    {
        value: PhoneType.Mobile,
        displayValue: <FormattedMessage id="app.select.option.mobile" />,
    },
    {
        value: PhoneType.Fax,
        displayValue: <FormattedMessage id="app.select.option.fax" />,
    },
];

export interface PhonesTableProps {
    /**
     * The main number as the first row. A member and a supplier have it in
     * their head instead (#2326 R-245).
     */
    withMain?: boolean;
    /**
     * A fixed "Mobil" row under the main number: a person is mostly reached
     * on the mobile, and adding one took "+" and a change of type (#2326
     * R-245, GF 27.10.2026). It holds the first mobile number, the one
     * documents and the contact card print; not required.
     */
    withMobile?: boolean;
}

export const PhonesTable = ({ withMain = true, withMobile = false }: PhonesTableProps) => {
    const classes = useStyles();
    const intl = useIntl();
    const formik = useFormikContext<OverviewPhoneFormValues>();
    const phones = formik.values.phones;
    const mobileIndex = withMobile ? phones.findIndex(phone => PhoneType.Mobile === phone.type) : -1;

    const handleNewPhone = (helper: PhoneArrayHelpers) => {
        helper.push({ id: new Date().getTime() * -1, number: '', type: PhoneType.Work });
    };

    // Typed into, the row becomes the first mobile entry; emptied, it goes
    // again rather than stand as a further number that must be filled.
    const changeMobile = (number: string) => {
        if (mobileIndex < 0) {
            if ('' !== number) {
                formik.setFieldValue('phones', [
                    ...phones,
                    { id: new Date().getTime() * -1, number, type: PhoneType.Mobile },
                ]);
            }
            return;
        }
        if ('' === number) {
            formik.setFieldValue(
                'phones',
                phones.filter((_, index) => index !== mobileIndex)
            );
            return;
        }
        formik.setFieldValue(`phones.${mobileIndex}.number`, number);
    };

    return (
        <>
            <SubtitleWithIcon title={<FormattedMessage id="app.common.phone" />} icon={<PhoneIcon />} />
            <Grid container={true} spacing={2}>
                {withMain && (
                    <>
                        <Grid item={true} xs={2}>
                            <PoolTypography
                                typography="body1"
                                component="div"
                                className={classes.static}
                                id="app.common.default"
                            />
                        </Grid>
                        <Grid item={true} xs={4}>
                            <FastField name={`mainPhone`}>
                                {({ field, form, meta }: FieldProps<string>) => (
                                    <PoolTextField
                                        required={true}
                                        placeholder={intl.formatMessage({
                                            id: 'app.contactsAndOrganizations.detail.examplePhone',
                                        })}
                                        fullWidth={true}
                                        error={!!meta.touched && !!meta.error}
                                        helperText={
                                            meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                        }
                                        {...field}
                                        value={field.value ?? ''}
                                    />
                                )}
                            </FastField>
                        </Grid>
                        <Grid item={true} xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton onClick={() => formik.setFieldValue('mainPhone', '')}>
                                <DeclineIcon />
                            </IconButton>
                        </Grid>
                    </>
                )}

                {withMobile && (
                    <>
                        <Grid item={true} xs={2}>
                            <PoolTypography
                                typography="body1"
                                component="div"
                                className={classes.static}
                                id="app.select.option.mobile"
                            />
                        </Grid>
                        <Grid item={true} xs={4}>
                            <PoolTextField
                                placeholder={intl.formatMessage({
                                    id: 'app.contactsAndOrganizations.detail.examplePhone',
                                })}
                                fullWidth={true}
                                name="mobilePhone"
                                value={mobileIndex < 0 ? '' : phones[mobileIndex].number ?? ''}
                                onChange={event => changeMobile(event.target.value)}
                            />
                        </Grid>
                        <Grid item={true} xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                            {mobileIndex >= 0 && (
                                <IconButton onClick={() => changeMobile('')}>
                                    <DeclineIcon />
                                </IconButton>
                            )}
                        </Grid>
                    </>
                )}

                <FieldArray name="phones">
                    {(helper: PhoneArrayHelpers) => {
                        return (
                            <>
                                {helper.form.values.phones.map((phone, index) =>
                                    index === mobileIndex ? null : (
                                        <Fragment key={phone.id}>
                                            <Grid item={true} xs={2}>
                                                <FastField name={`phones.${index}.type`}>
                                                    {({ field, form }: FieldProps<PhoneType>) => (
                                                        <PoolSelect
                                                            fullWidth={true}
                                                            disallowDefaultValue={true}
                                                            onValueChange={value =>
                                                                form.setFieldValue(field.name, value)
                                                            }
                                                            value={field.value}
                                                            name={field.name}
                                                            noSorting={true}
                                                            options={phoneTypeOptions}
                                                        />
                                                    )}
                                                </FastField>
                                            </Grid>
                                            <Grid item={true} xs={4}>
                                                <FastField name={`phones.${index}.number`}>
                                                    {({ field, form, meta }: FieldProps<string>) => (
                                                        <PoolTextField
                                                            required={true}
                                                            placeholder={intl.formatMessage({
                                                                id: 'app.contactsAndOrganizations.detail.examplePhone',
                                                            })}
                                                            fullWidth={true}
                                                            error={!!meta.touched && !!meta.error}
                                                            helperText={
                                                                meta.touched && meta.error ? (
                                                                    <FormattedMessage id={meta.error} />
                                                                ) : null
                                                            }
                                                            {...field}
                                                            value={field.value ?? ''}
                                                        />
                                                    )}
                                                </FastField>
                                            </Grid>
                                            <Grid item={true} xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <IconButton onClick={() => helper.remove(index)}>
                                                    <DeclineIcon />
                                                </IconButton>
                                            </Grid>
                                        </Fragment>
                                    )
                                )}

                                <Grid item={true} xs={12}>
                                    <PoolButton
                                        styleType="thirdLevel"
                                        startIcon={<AddIcon color="primary" />}
                                        onClick={() => handleNewPhone(helper)}
                                    >
                                        <FormattedMessage id="app.contactsAndOrganizations.detail.addPhone" />
                                    </PoolButton>
                                </Grid>
                            </>
                        );
                    }}
                </FieldArray>
            </Grid>
        </>
    );
};
